import * as React from 'react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { IconName, SVGIcon } from '../SVGIcon';
import clsx from 'clsx';

// Funkcja płynnego przewijania do elementu
const smoothScrollToElement = (elementId: string, offset: number = 0) => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

// Komponent dla pojedynczego SubItem - zmemoizowany
const SubItemComponent = React.memo<{
  subItem: SubItem;
  parentItem: AccordionItem;
  isActive: boolean;
  onSubItemClick: (subItem: SubItem, parentItem: AccordionItem) => void;
}>(({ subItem, parentItem, isActive, onSubItemClick }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onSubItemClick(subItem, parentItem);
    },
    [subItem, parentItem, onSubItemClick],
  );

  return (
    <div>
      <a
        href="#"
        onClick={handleClick}
        className={clsx(
          'group block w-full rounded-xl pb-2 transition-colors duration-200',
          'hover:text-primary-500',
          {
            'text-primary-500': isActive,
            'text-muted': !isActive,
          },
        )}
      >
        <div className="flex w-full items-center justify-between text-left text-sm font-medium">
          <div className="flex items-center gap-3 font-medium">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid meet"
              className={clsx({
                'stroke-primary': isActive,
                'stroke-text-muted group-hover:stroke-primary': !isActive,
              })}
            >
              <circle cx="7" cy="7" r="5.5" strokeWidth="3" />
            </svg>
            <span
              className={clsx('transition-colors duration-200', {
                'text-primary': isActive,
                'text-text-muted group-hover:text-primary': !isActive,
              })}
            >
              {subItem.label}
            </span>
          </div>
        </div>
      </a>
    </div>
  );
});

SubItemComponent.displayName = 'SubItemComponent';

// Komponent dla pojedynczego Accordion Item - zmemoizowany
const AccordionItemComponent = React.memo<{
  item: AccordionItem;
  activeElementId: string | null;
  openItems: string[];
  onItemClick: (item: AccordionItem) => void;
  onSubItemClick: (subItem: SubItem, parentItem: AccordionItem) => void;
}>(({ item, activeElementId, openItems, onItemClick, onSubItemClick }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onItemClick(item);
    },
    [item, onItemClick],
  );

  // Sprawdź czy którykolwiek child jest aktywny
  const hasActiveChild = useMemo(() => {
    if (!item.items || !activeElementId) return false;
    return item.items.some((subItem) => subItem.elementId === activeElementId);
  }, [item.items, activeElementId]);

  // Rodzic jest aktywny jeśli on sam jest aktywny LUB którykolwiek z jego dzieci jest aktywny
  const isActive = item.elementId === activeElementId || hasActiveChild;
  const isOpen = openItems.includes(item.value);

  // Element bez dzieci - zwykły link
  if (!item.items || item.items.length === 0) {
    return (
      <a
        href="#"
        onClick={handleClick}
        className={clsx(
          'group block w-full rounded-xl transition-colors duration-200',
          'hover:bg-primary-500 hover:text-white',
          {
            'bg-primary-500 text-white': isActive,
            'bg-transparent': !isActive,
          },
        )}
      >
        <div className="flex w-full items-center justify-between text-left text-sm font-medium">
          <div className="flex items-center gap-5 p-3 font-medium">
            <SVGIcon
              name={item.icon as IconName}
              fill={isActive ? 'white' : undefined}
              className={clsx('transition-colors duration-200', {
                'fill-white': isActive,
                'fill-gray-500 group-hover:fill-white': !isActive,
              })}
            />
            <span
              className={clsx('transition-colors duration-200', {
                'text-white': isActive,
                'text-text-muted group-hover:text-white': !isActive,
              })}
            >
              {item.label}
            </span>
          </div>
        </div>
      </a>
    );
  }

  // Element z dziećmi - accordion
  return (
    <>
      <Accordion.Trigger
        className={clsx(
          'group block w-full rounded-xl transition-colors duration-200',
          'hover:bg-primary-500 hover:text-white',
          {
            'bg-primary-500 text-white': isActive,
            'bg-transparent': !isActive,
          },
        )}
      >
        <div className="flex w-full items-center justify-between text-left text-sm font-medium">
          <div className="flex items-center gap-5 p-3 font-medium">
            <SVGIcon
              name={item.icon as IconName}
              fill={isActive ? 'white' : undefined}
              className={clsx('', {
                'fill-white': isActive,
                'fill-gray-500 group-hover:fill-white': !isActive,
              })}
            />
            <span
              className={clsx('transition-colors duration-200', {
                'text-white': isActive,
                'text-text-muted group-hover:text-white': !isActive,
              })}
            >
              {item.label}
            </span>
          </div>
          <div
            className={clsx(
              'relative mr-3 h-4 w-4 flex-shrink-0 rounded-[4px] border-2 p-2',
              {
                'border-blueLight': isActive,
                'border-gray-500 group-hover:border-blueLight': !isActive,
              },
            )}
          >
            <SVGIcon
              name="chevron-square"
              height={15}
              width={15}
              className={clsx(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'transform transition-all duration-200',
                {
                  'rotate-0': isOpen,
                  'rotate-[-90deg]': !isOpen,
                  'fill-blueLight': isActive,
                  'fill-gray-500 group-hover:fill-blueLight': !isActive,
                },
              )}
            />
          </div>
        </div>
      </Accordion.Trigger>
      <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden pt-3 text-sm">
        <div className="ml-6 border-l-2 border-grayBorder pl-6 pr-2">
          {item.items?.map((subItem, index) => (
            <SubItemComponent
              key={subItem.elementId || index}
              subItem={subItem}
              parentItem={item}
              isActive={subItem.elementId === activeElementId}
              onSubItemClick={onSubItemClick}
            />
          ))}
        </div>
      </Accordion.Content>
    </>
  );
});

AccordionItemComponent.displayName = 'AccordionItemComponent';

// Typ dla poszczególnych podlinków
export interface SubItem {
  label: string;
  path?: string;
  active?: boolean;
  icon?: IconName;
  elementId?: string; // ID elementu do przewinięcia
}

// Typ dla głównych sekcji akordeonu
export interface AccordionItem {
  label: string;
  value: string;
  icon?: IconName;
  defaultOpen?: boolean;
  items?: SubItem[];
  active?: boolean;
  elementId?: string; // ID elementu do przewinięcia
}

export interface SidebarAccordionProps {
  items: AccordionItem[];
  onItemClick?: (item: AccordionItem) => void;
  onSubItemClick?: (subItem: SubItem, parentItem: AccordionItem) => void;
  scrollOffset?: number; // Offset w pikselach dla przewijania (domyślnie 0)
  onActiveItemChange?: (activeElementId: string | null) => void; // Callback wywoływany gdy zmienia się aktywna sekcja
  enableScrollTracking?: boolean; // Czy włączyć śledzenie przewijania (domyślnie true)
}

export const SidebarAccordion: React.FC<SidebarAccordionProps> = ({
  items,
  onItemClick,
  onSubItemClick,
  scrollOffset = 0,
  onActiveItemChange,
  enableScrollTracking = true,
}) => {
  const [openItems, setOpenItems] = useState<string[]>(
    items
      ? items.filter((item) => item.defaultOpen).map((item) => item.value)
      : [],
  );

  const [activeElementId, setActiveElementId] = useState<string | null>(() => {
    // Sprawdź czy jest hash w URL przy inicjalizacji
    if (typeof window !== 'undefined' && window.location.hash) {
      return window.location.hash.substring(1);
    }
    return null;
  });
  const [isUserScrolling, setIsUserScrolling] = useState(false);

  // Referencje do sekcji dla Intersection Observer
  const sectionRefs = useRef<Map<string, Element>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoizuj wszystkie elementId z items
  const allElementIds = useMemo(() => {
    const ids: string[] = [];
    items.forEach((item) => {
      if (item.elementId) {
        ids.push(item.elementId);
      }
      if (item.items) {
        item.items.forEach((subItem) => {
          if (subItem.elementId) {
            ids.push(subItem.elementId);
          }
        });
      }
    });
    return ids;
  }, [items]);

  // Funkcja do aktualizacji URL z aktualnym elementId
  const updateUrlHash = useCallback(
    (elementId: string | null) => {
      if (elementId && !isUserScrolling) {
        const newUrl = elementId ? `#${elementId}` : '';
        if (window.location.hash !== newUrl) {
          window.history.replaceState(null, '', newUrl);
        }
      }
    },
    [isUserScrolling],
  );

  // Callback dla Intersection Observer
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (!enableScrollTracking || isUserScrolling) return;

      // Znajdź sekcję, która jest najbardziej widoczna
      let mostVisibleSection = '';
      let highestRatio = 0;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
          highestRatio = entry.intersectionRatio;
          const elementId = entry.target.getAttribute('id');
          if (elementId) {
            mostVisibleSection = elementId;
          }
        }
      });

      // Przełącz aktywny element tylko jeśli znaleziono widoczną sekcję
      if (mostVisibleSection && mostVisibleSection !== activeElementId) {
        setActiveElementId(mostVisibleSection);
        updateUrlHash(mostVisibleSection);
        onActiveItemChange?.(mostVisibleSection);
      }
    },
    [
      activeElementId,
      enableScrollTracking,
      isUserScrolling,
      updateUrlHash,
      onActiveItemChange,
    ],
  );

  // Inicjalizacja Intersection Observer
  useEffect(() => {
    if (!enableScrollTracking || allElementIds.length === 0) return;

    // Konfiguracja observer
    const options: IntersectionObserverInit = {
      root: null,
      // Sekcja jest aktywna gdy jest w górnych 30% ekranu
      rootMargin: `${-scrollOffset}px 0px -70% 0px`,
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
    };

    // Utwórz nowy observer
    const observer = new IntersectionObserver(handleIntersection, options);
    observerRef.current = observer;

    // Obserwuj wszystkie sekcje
    const currentSections = sectionRefs.current;
    allElementIds.forEach((elementId) => {
      const element = document.getElementById(elementId);
      if (element && observer) {
        currentSections.set(elementId, element);
        observer.observe(element);
      }
    });

    // Cleanup
    return () => {
      if (observer) {
        observer.disconnect();
      }
      currentSections.clear();
    };
  }, [allElementIds, enableScrollTracking, scrollOffset, handleIntersection]);

  // Wspólna logika scrollowania do sekcji
  const scrollToSection = useCallback(
    (elementId: string) => {
      // Włącz flagę user scrolling aby wyłączyć automatyczne śledzenie
      setIsUserScrolling(true);
      setActiveElementId(elementId);
      updateUrlHash(elementId);
      smoothScrollToElement(elementId, scrollOffset);
      onActiveItemChange?.(elementId);

      // Wyczyść poprzedni timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Wyłącz flagę scrolling po zakończeniu animacji
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 1500);
    },
    [scrollOffset, updateUrlHash, onActiveItemChange],
  );

  const handleValueChange = useCallback((value: string[]) => {
    setOpenItems(value);
  }, []);

  const handleItemClick = useCallback(
    (item: AccordionItem) => {
      // Jeśli element ma elementId, przewiń do niego
      if (item.elementId) {
        scrollToSection(item.elementId);
      }
      // Wywołaj callback jeśli istnieje
      onItemClick?.(item);
    },
    [scrollToSection, onItemClick],
  );

  const handleSubItemClick = useCallback(
    (subItem: SubItem, parentItem: AccordionItem) => {
      // Jeśli podelement ma elementId, przewiń do niego
      if (subItem.elementId) {
        scrollToSection(subItem.elementId);
      }
      // Wywołaj callback jeśli istnieje
      onSubItemClick?.(subItem, parentItem);
    },
    [scrollToSection, onSubItemClick],
  );

  // Automatycznie otwórz accordion jeśli aktywna sekcja jest podsekcją
  useEffect(() => {
    if (!activeElementId) return;

    // Znajdź rodzica dla aktywnego elementu
    items.forEach((item) => {
      if (item.items) {
        const hasActiveSubItem = item.items.some(
          (subItem) => subItem.elementId === activeElementId,
        );
        if (hasActiveSubItem) {
          setOpenItems((prev) => {
            // Dodaj tylko jeśli nie jest już otwarty
            if (!prev.includes(item.value)) {
              return [...prev, item.value];
            }
            return prev;
          });
        }
      }
    });
  }, [activeElementId, items]);

  // Przewiń do sekcji z URL hash przy inicjalizacji
  useEffect(() => {
    const initialHash =
      typeof window !== 'undefined' && window.location.hash
        ? window.location.hash.substring(1)
        : null;

    if (initialHash) {
      const element = document.getElementById(initialHash);
      if (element) {
        // Odczekaj chwilę aby DOM się załadował
        setTimeout(() => {
          smoothScrollToElement(initialHash, scrollOffset);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Uruchom tylko raz przy montowaniu

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Accordion.Root
      type="multiple"
      className="space-y-2 px-2"
      value={openItems}
      onValueChange={handleValueChange}
    >
      {items.map((item) => (
        <Accordion.Item
          key={item.value}
          value={item.value}
          className="overflow-hidden"
        >
          <AccordionItemComponent
            item={item}
            activeElementId={activeElementId}
            openItems={openItems}
            onItemClick={handleItemClick}
            onSubItemClick={handleSubItemClick}
          />
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
};

export default SidebarAccordion;
